// Shared font utilities

// Map font family values to proper font stacks
export const fontStacks: Record<string, string> = {
  // Default stacks
  sans: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  serif: "ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif",
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  script: "'Dancing Script', 'Brush Script MT', cursive",
  "system-ui": "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
  
  // Popular Sans-serif fonts (prioritized)
  roboto: "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  inter: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  montserrat: "Montserrat, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  "open-sans": "'Open Sans', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  "plus-jakarta-sans": "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  
  // Other Sans-serif fonts
  arial: "Arial, Helvetica, sans-serif",
  helvetica: "Helvetica, Arial, sans-serif",
  verdana: "Verdana, Geneva, sans-serif",
  tahoma: "Tahoma, Geneva, Verdana, sans-serif",
  trebuchet: "'Trebuchet MS', Helvetica, sans-serif",
  "gill-sans": "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
  "segoe-ui": "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  "noto-sans": "'Noto Sans', Arial, sans-serif",
  lato: "Lato, Arial, sans-serif",
  poppins: "Poppins, Arial, sans-serif",
  nunito: "Nunito, Arial, sans-serif",
  raleway: "Raleway, Arial, sans-serif",
  ubuntu: "Ubuntu, Arial, sans-serif",
  oswald: "Oswald, Arial, sans-serif",
  "source-sans": "'Source Sans Pro', Arial, sans-serif",
  
  // Popular Serif fonts (prioritized)
  playfair: "'Playfair Display', Georgia, 'Times New Roman', serif",
  "dm-serif-display": "'DM Serif Display', Georgia, 'Times New Roman', serif",
  merriweather: "Merriweather, Georgia, 'Times New Roman', serif",
  
  // Other Serif fonts
  times: "'Times New Roman', Times, serif",
  georgia: "Georgia, serif",
  palatino: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
  bookman: "'Bookman Old Style', serif",
  garamond: "Garamond, serif",
  baskerville: "Baskerville, 'Baskerville Old Face', 'Hoefler Text', Garamond, 'Times New Roman', serif",
  didot: "Didot, 'Didot LT STD', 'Hoefler Text', Garamond, 'Times New Roman', serif",
  cambria: "Cambria, Georgia, serif",
  lora: "Lora, Georgia, serif",
  crimson: "'Crimson Text', Georgia, serif",
  bitter: "Bitter, Georgia, serif",
  "libre-baskerville": "'Libre Baskerville', Baskerville, Georgia, serif",
  "eb-garamond": "'EB Garamond', Garamond, serif",
  
  // Monospace fonts
  courier: "'Courier New', Courier, monospace",
  "lucida-console": "'Lucida Console', Monaco, monospace",
  monaco: "Monaco, 'Courier New', monospace",
  consolas: "Consolas, Monaco, 'Courier New', monospace",
  "source-code": "'Source Code Pro', Monaco, monospace",
  "fira-code": "'Fira Code', Monaco, monospace",
  "jetbrains-mono": "'JetBrains Mono', Monaco, monospace",
  "ibm-plex-mono": "'IBM Plex Mono', Monaco, monospace",
  "roboto-mono": "'Roboto Mono', Monaco, monospace",
  "space-mono": "'Space Mono', Monaco, monospace",
  "ubuntu-mono": "'Ubuntu Mono', Monaco, monospace",
  
  // Display & Script fonts
  impact: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
  "comic-sans": "'Comic Neue', 'Comic Sans MS', 'Comic Sans', cursive",
  "brush-script": "'Brush Script MT', cursive",
  copperplate: "Copperplate, 'Copperplate Gothic Light', fantasy",
  papyrus: "Papyrus, fantasy",
  "bebas-neue": "'Bebas Neue', Impact, sans-serif",
  anton: "Anton, Impact, sans-serif",
  righteous: "Righteous, cursive",
  pacifico: "Pacifico, cursive",
  "dancing-script": "'Dancing Script', cursive",
  "great-vibes": "'Great Vibes', cursive",
  satisfy: "Satisfy, cursive",
  "kaushan-script": "'Kaushan Script', cursive",
  allura: "Allura, cursive",
};

/**
 * Get the proper CSS font stack for a given font family value
 */
export function getFontStack(fontFamily: string | undefined, fallback?: string): string {
  // If an explicit key is provided and known, return its stack
  if (fontFamily && fontStacks[fontFamily]) {
    return fontStacks[fontFamily];
  }

  // If a string font family is provided (not a known key), assume it's a literal
  // Google Font family name and fall back to a sensible generic.
  if (fontFamily && !fontStacks[fontFamily]) {
    const generic = "sans-serif";
    // Wrap with quotes if contains spaces
    const needsQuotes = /\s/.test(fontFamily);
    const literal = needsQuotes ? `'${fontFamily}'` : fontFamily;
    return `${literal}, ${generic}`;
  }

  // If a fallback key is provided, prefer its mapped stack when available
  if (fallback) {
    return fontStacks[fallback] || fallback;
  }

  // Default to sans stack
  return fontStacks.sans;
}

/**
 * Normalize a font name for comparison (handles both slugs and friendly names)
 */
export function normalizeFontName(name: string | undefined): string {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, "-")
    .trim();
}

/**
 * Convert a slugged font name to a friendly display name
 * Example: "open-sans" -> "Open Sans"
 */
export function slugToFriendlyName(slug: string | undefined): string {
  if (!slug) return "";
  return slug
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Check if two font names match (handles slug vs friendly name comparison)
 */
export function fontNamesMatch(name1: string | undefined, name2: string | undefined): boolean {
  if (!name1 || !name2) return false;
  return normalizeFontName(name1) === normalizeFontName(name2);
}

/**
 * Check if a font family value is a Google Font
 */
export function isGoogleFont(fontFamily: string): boolean {
  const googleFonts = [
    // Popular fonts
    "roboto", "inter", "montserrat", "open-sans", "plus-jakarta-sans",
    "playfair", "dm-serif-display", "merriweather",
    // Other Sans-serif
    "lato", "poppins", "nunito", "raleway", "ubuntu", "oswald", "source-sans", "noto-sans",
    // Other Serif
    "lora", "crimson", "bitter", "libre-baskerville", "eb-garamond",
    // Monospace
    "source-code", "fira-code", "jetbrains-mono", "ibm-plex-mono", "roboto-mono",
    "space-mono", "ubuntu-mono",
    // Display
    "bebas-neue", "anton", "righteous", "pacifico",
    "dancing-script", "great-vibes", "satisfy", "kaushan-script", "allura"
  ];
  
  return googleFonts.includes(fontFamily);
}